/**
 * Types TypeScript pour le module POS
 */

export type TaxClass = 'STANDARD_21' | 'REDUIT_12' | 'ALIM_6' | 'ZERO_0';
export type PaymentMethod = 'cash' | 'bancontact' | 'meal_voucher' | 'wero' | 'wallet' | 'online' | 'virement' | 'mixed';
export type VendorRole = 'ADMIN' | 'MANAGER' | 'CASHIER' | 'CASHIER_PLUS' | 'FIELD';

// Rôles ayant accès aux fonctionnalités étendues (Live Shopping, Préparation)
export const ROLES_WITH_FULL_ACCESS: VendorRole[] = ['ADMIN', 'MANAGER', 'CASHIER_PLUS'];

// Vérifie si un rôle a accès aux fonctionnalités étendues
export const hasFullAccessRole = (role: VendorRole): boolean =>
  ROLES_WITH_FULL_ACCESS.includes(role);

// Vendor Types
export interface Vendor {
  id: string;
  tenant_id?: string;
  name: string;
  email?: string;
  role: VendorRole;
  active: boolean;
  has_pin?: boolean; // Indique si le vendeur a un PIN configuré
  /** Position sur la barre vendeurs du POS (croissant). */
  display_order?: number;
  created_at: string;
  updated_at?: string;
}

export interface VendorSession {
  session_id: string;
  vendor_id: string;
  workstation_id: string;
  mode: 'online' | 'offline';
  cart_snapshot?: {
    lines: CartLine[];
  };
}

export interface VerifyPinRequest {
  pin: string;
}

export interface VerifyPinResponse {
  valid: boolean;
}

export interface AcquireSessionRequest {
  workstation_id: string;
  force?: boolean;
}

export interface SessionLockError {
  code: 'SESSION_LOCKED';
  message: string;
  workstation: string;
}

/** Remise anti-gaspi portée par un lot (servie par le catalogue, voir common/promo-lot.ts côté API). */
export interface LotPromo {
  lot_id: string;
  lot_number: string;
  expiry_date: string | null;
  percent: number;
  /** Prix remisé (centimes), unitaire ou au kilo selon le produit. */
  price_cents: number;
}

export interface Product {
  id: string;
  code?: string | null;
  name: string;
  barcode?: string | null;
  is_weighted?: boolean;
  unit?: string | null;
  tax_class?: TaxClass | string | null;
  image_url?: string | null;
  description?: string | null;
  is_deposit?: boolean;
  deposit_qty_per_unit?: number | null;
  deposit_product_id?: string | null;
  /** Lots remisés en stock : la caisse demande au scan lequel le client a en main. */
  lot_promos?: LotPromo[];
  /** DLC la plus proche parmi les autres lots en stock (celle du « prix normal »). */
  lot_normal_expiry_date?: string | null;
  active?: boolean;
  stock_quantity?: number;
  stock_backroom?: number;
  stock_display?: number;
  supplier_name?: string | null;
  producer_name?: string | null;
  origin_country?: string | null;
  bio?: boolean;
  vegan?: boolean;
  organic_certifier?: string | null;
  expiry_date?: string | null;
  purchase_price_cents?: number | null;
  suggested_price_cents?: number | null;
  gross_weight_grams?: number | null;
  variable_weight?: boolean;
  restock_alert_threshold?: number | null;
  order_threshold?: number | null;
  category_id?: string | null;
  sell_by_pieces?: boolean;
  avg_weight_per_piece_grams?: number | null;
  /** Eshop : à la pièce uniquement (sans effet au POS, qui pèse toujours). */
  ecom_pieces_only?: boolean;
  ecom_pieces_first?: boolean;
  /** Imprimer la DLC du lot FEFO sur le ticket client (œufs en vrac...) */
  print_dlc_on_ticket?: boolean;
  /** DLC la plus proche parmi les lots en rayon (calculée par le catalogue) */
  display_expiry_date?: string | null;
  pack_size?: number | null;
  preorder_enabled?: boolean;
  availability_days?: ('FRIDAY' | 'SATURDAY')[];
  category?: {
    id: string;
    name: string;
    slug: string;
  };
  prices?: {
    id: string;
    currency: string;
    amount_cents: number;
  }[];
}

export interface Price {
  id: string;
  product_id: string;
  currency: string;
  amount_cents: number;
}

export interface CartLine {
  /** Identifiant de l'ENCODAGE qui a créé cette ligne. Un même encodage peut produire
   *  2 lignes (l'article + sa consigne) : elles partagent le même op_id pour qu'une
   *  annulation les retire ensemble. Absent sur les paniers d'avant août 2026. */
  op_id?: number;
  product: Product | null;
  product_name?: string; // Nom libre pour lignes sans produit catalogue
  note?: string;         // Note justificative (montant libre, remboursement)
  qty: number;
  unit_price_cents: number;
  tax_rate?: number;     // TVA pour lignes sans produit (default 21%)
  is_deposit?: boolean;
  is_return?: boolean;
  is_custom?: boolean;   // Ligne montant libre
  is_refund?: boolean;   // Remboursement sans article
  /** Vente d'un bon cadeau : argent reçu d'avance, donc une DETTE — ni CA ni TVA.
   *  Le serveur force le taux à 0 et crée le bon dans la transaction de la vente. */
  is_gift_voucher?: boolean;
  /** Lot choisi au scan (remise anti-gaspi) : la vente décompte CE lot. */
  lot_id?: string;
  lot_promo_percent?: number;
  lot_expiry_date?: string | null;
  /** Prix unitaire avant remise, pour le ticket et la trace de la remise. */
  original_price_cents?: number;
}

export interface SaleCustomer {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  vat_number?: string;  // Numéro TVA pour facturation
}

export interface Sale {
  id: string;
  sale_number: string;
  vendor_id?: string | null;
  vendor?: Vendor | null;
  vendor_name?: string | null;  // Nom dénormalisé (pour analytics/historique)
  customer_id?: string | null;
  customer?: SaleCustomer | null;  // Client associé à la vente
  payment_method: PaymentMethod;
  payment_metadata?: Record<string, number> | null;
  // Noms mappés depuis le backend
  total_ttc_cents?: number;  // Mappé depuis total_cents
  total_ht_cents?: number;   // Calculé
  total_tva_cents?: number;  // Mappé depuis tax_total_cents
  // Noms originaux de la DB (pour rétrocompatibilité)
  total_cents?: number;
  tax_total_cents?: number;
  // Détails paiement
  amount_paid_cents?: number | null;  // Montant reçu du client
  change_cents?: number | null;       // Rendu monnaie
  status?: string;
  created_at: Date | string;
  lines?: SaleLine[];
  // Facturation Enfact
  invoice_requested?: boolean;  // Client demande une facture
  enfact_invoice?: {
    id: string;
    enfact_invoice_id?: number;
    enfact_invoice_number?: string;
    status: string;
    peppol_status?: string;
  } | null;
}

export interface SaleLine {
  id: string;
  sale_id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price_cents: number;
  tax_rate: number;
  is_deposit?: boolean;
  is_return?: boolean;
}

export interface CreateSaleDto {
  tenant_id: string;
  vendor_id?: string;
  customer_id?: string;
  payment_method: PaymentMethod;
  amount_paid_cents?: number;  // Montant reçu du client (espèces)
  lines: {
    product_id?: string;
    product_name: string;
    quantity: number;
    unit_price_cents: number;
    tax_rate: number;
    is_deposit?: boolean;
    is_return?: boolean;
  }[];
}

export interface SaleTotals {
  subtotal_ht_cents: number;
  tax_total_cents: number;
  total_ttc_cents: number;
  subtotal_ht_euros: string;
  tax_total_euros: string;
  total_ttc_euros: string;
}

export interface StockAlertInfo {
  type: 'SHELF_EMPTY' | 'SHELF_LOW' | 'ORDER_SUGGESTED';
  productId: string;
  productName: string;
  stockDisplay: number;
  stockBackroom: number;
  stockTotal?: number;
  threshold?: number;
}

export interface CreateSaleResponse {
  success: boolean;
  sale: Sale;
  lines: SaleLine[];
  totals: SaleTotals;
  wallet_transaction?: any;
  stockAlerts?: StockAlertInfo[];
}
